import debounce from 'lodash/debounce';
import { useEffect, useMemo, useState } from 'react';

import {
  useGetMedicalDictionaryQuery,
  useLazyGetMedicalDictionaryQuery,
} from '@app/api/Dictionaries';
import { useUserPrint } from '@app/hooks';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { handleCatchError } from '@app/services';

import type { MedicalDictionaryType } from '../data';

export const useDictionary = (
  type: MedicalDictionaryType,
  value?: string,
  isStatic?: boolean
) => {
  const { activeBranchId: ActiveBranchId } = useUserPrint();
  const { showNotification } = useNotification();

  const { data: staticData, isLoading: isStaticLoading } =
    useGetMedicalDictionaryQuery({ type, ActiveBranchId }, { skip: !isStatic });

  const [fetch, { data, isFetching, error }] =
    useLazyGetMedicalDictionaryQuery();

  const [loadedValue, setLoadedValue] = useState<string>();

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        if (value.length >= 3) {
          fetch({ type, search: value, ActiveBranchId });
        }
      }, 400),
    [ActiveBranchId, fetch, type]
  );

  useEffect(() => {
    if (value && value !== loadedValue) {
      fetch({
        type,
        code: value,
        ActiveBranchId,
      });

      setLoadedValue(value);
    }
  }, [value, loadedValue, type, ActiveBranchId, fetch]);

  useEffect(() => {
    if (error) {
      showNotification(handleCatchError(error));
    }
  }, [error, showNotification]);

  return {
    options: (isStatic ? staticData?.Items : data?.Items) || [],
    isLoading: isStatic ? isStaticLoading : isFetching,
    onSearch: isStatic ? undefined : debouncedSearch,
  };
};
